import { useStore } from "vuex";
import { computed, watch } from "vue";
import { MODULES, vuexName } from "@/store/utils";
import { AnnotationObject } from "@/store/annotation-v2/state";
import { fabric } from "fabric";
import useCanvas from "@/hooks/use-canvas";
import { isLessonBackgroundObject } from "@/utils/fabric-utils";

/**
 * Hook to handle full synchronization of canvas objects
 * Watches the objects array and performs complete re-render when it changes
 * Triggers on: slide change, entering/exiting 1:1 mode, initial class load
 */
export const useCanvasFullSync = () => {
  const { getters } = useStore();

  // Get current objects based on mode (normal vs 1:1)
  const objects = computed<AnnotationObject[]>(() => getters[vuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS)]);

  /**
   * Clear all canvas objects except lesson-img and targets
   */
  const clearCanvasObjects = () => {
    const [canvas] = useCanvas();

    if (!canvas) return;

    const objectsToRemove = canvas.getObjects().filter((obj: any) => {
      // Keep lesson-img (the slide background image)
      if (isLessonBackgroundObject(obj)) return false;
      // Keep target objects (interactive elements on slides)
      if (obj.targetData) return false;
      return true;
    });

    objectsToRemove.forEach((obj: any) => canvas.remove(obj));
  };

  /**
   * Render all objects from the objects array onto canvas
   * Objects from BE are JSON strings of Fabric objects
   *
   * IMPORTANT: Objects added here are from server state, NOT new drawings
   * We mark them with skipCallback flag to prevent infinite loop:
   * Add → onObjectCreated → send WS → handler → add → loop...
   */
  const renderAllObjects = (objectsList: AnnotationObject[]) => {
    const [canvas] = useCanvas();

    if (!canvas || !objectsList || objectsList.length === 0) return;

    objectsList.forEach((obj) => {
      if (!obj.metadata) return;

      try {
        // Parse the Fabric object JSON from metadata
        const fabricObjectJson = JSON.parse(obj.metadata);

        // Use Fabric's enlivenObjects to recreate the object with all its properties
        fabric.util.enlivenObjects([fabricObjectJson], (fabricObjects: any[]) => {
          if (fabricObjects && fabricObjects.length > 0) {
            const fabricObj = fabricObjects[0];

            // Mark this object to skip onObjectCreated callback
            // This prevents infinite loop: add → send → receive → add → send...
            fabricObj.skipCallback = true;

            // objectId is already in the fabricObjectJson, but ensure it's preserved
            if (!fabricObj.objectId && fabricObjectJson.objectId) {
              fabricObj.objectId = fabricObjectJson.objectId;
            }

            canvas.add(fabricObj);

            // Remove flag after adding to avoid side effects
            delete fabricObj.skipCallback;
          }
        });
      } catch (error) {
        console.error("Failed to render object:", obj.objectId || obj.metadata?.substring(0, 50), error);
      }
    });

    canvas.renderAll();
  };

  /**
   * Full sync: clear all and re-render from objects array
   */
  const performFullSync = (newObjects: AnnotationObject[]) => {
    clearCanvasObjects();
    renderAllObjects(newObjects);
  };

  // Watch objects array for changes and trigger full sync
  watch(
    objects,
    (newObjects) => {
      performFullSync(newObjects);
    },
    { deep: true },
  );

  return { performFullSync };
};
